import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import CareerTimeline from '../components/hr/CareerTimeline';

const fmt      = (v) => v || '—';
const fmtDate  = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const fmtINR   = (n) => n ? `₹${parseFloat(n).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '—';
const fmtMonth = (m) => {
  if (!m) return '—';
  const [y, mo] = m.split('-');
  return new Date(+y, +mo - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
};

const STATUS_META = {
  active:      { label: 'Active',      bg: '#d1fae5', color: '#065f46' },
  on_leave:    { label: 'On Leave',    bg: '#fef3c7', color: '#92400e' },
  on_notice:   { label: 'On Notice',   bg: '#ffedd5', color: '#c2410c' },
  resigned:    { label: 'Resigned',    bg: '#fee2e2', color: '#991b1b' },
  terminated:  { label: 'Terminated',  bg: '#fce7f3', color: '#9d174d' },
};
const EMP_TYPE_LABELS = {
  full_time: 'Full Time', part_time: 'Part Time', contract: 'Contract', intern: 'Intern',
};

function InfoRow({ label, value }) {
  return (
    <div style={{ display: 'flex', padding: '9px 0', borderBottom: '1px solid var(--border)' }}>
      <div style={{ width: 180, flexShrink: 0, fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 500 }}>{label}</div>
      <div style={{ flex: 1, fontSize: '0.875rem', color: 'var(--text-primary)', fontWeight: 500 }}>{value}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', color: 'var(--text-muted)', marginBottom: 4 }}>{title}</div>
      <div className="card"><div className="card-body" style={{ padding: '4px 16px 4px' }}>{children}</div></div>
    </div>
  );
}

export default function MyProfile() {
  const { user } = useAuth();
  const [tab, setTab]         = useState('details');
  const [emp, setEmp]         = useState(null);
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payslipsLoading, setPayslipsLoading] = useState(false);
  const [error, setError]     = useState('');
  const [downloading, setDownloading] = useState(null);

  const loadProfile = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const { data } = await api.get('/hr/me');
      setEmp(data.data);
    } catch (e) {
      setError(e.response?.data?.message || 'Could not load employee profile.');
    } finally { setLoading(false); }
  }, []);

  const loadPayslips = useCallback(async () => {
    setPayslipsLoading(true);
    try {
      const { data } = await api.get('/hr/me/payslips');
      setPayslips(data.data || []);
    } finally { setPayslipsLoading(false); }
  }, []);

  useEffect(() => { loadProfile(); }, [loadProfile]);
  useEffect(() => { if (tab === 'payslips' && emp) loadPayslips(); }, [tab, emp, loadPayslips]);

  const downloadPayslip = async (id, month) => {
    setDownloading(id);
    try {
      const resp = await api.get(`/hr/me/payslips/${id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payslip-${month}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert('Failed to download payslip. Please try again.');
    } finally { setDownloading(null); }
  };

  const statusMeta = STATUS_META[emp?.status] || { label: emp?.status || '—', bg: '#f1f5f9', color: '#64748b' };
  const fullName = emp ? `${emp.first_name} ${emp.last_name || ''}`.trim() : user?.name || '';
  const initials = fullName.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();
  const hue = (fullName?.charCodeAt(0) || 65) * 17 % 360;

  if (loading) return <div className="page"><div className="loading-overlay"><div className="spinner spinner-lg" /></div></div>;

  if (error) return (
    <div className="page">
      <div className="page-header">
        <div><h1 className="page-title">My Employment</h1></div>
      </div>
      <div className="card">
        <div className="card-body" style={{ textAlign: 'center', padding: 40 }}>
          <div style={{ fontSize: '2rem', marginBottom: 12 }}>🪪</div>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>No Employee Record Found</div>
          <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>{error}</div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">My Employment</h1>
          <p className="page-subtitle">Your employment details and salary slips</p>
        </div>
      </div>

      {/* Profile header card */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-body" style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
          <div style={{
            width: 64, height: 64, borderRadius: 16, flexShrink: 0,
            background: `hsl(${hue},50%,88%)`, color: `hsl(${hue},50%,30%)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 800, fontSize: '1.4rem',
          }}>{initials}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>{fullName}</div>
            <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginTop: 2 }}>
              {fmt(emp.designation)}{emp.department ? ` · ${emp.department}` : ''}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: '0.75rem', fontWeight: 600, background: statusMeta.bg, color: statusMeta.color }}>{statusMeta.label}</span>
              {emp.employment_type && <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: '0.75rem', fontWeight: 600, background: '#ede9fe', color: '#5b21b6' }}>{EMP_TYPE_LABELS[emp.employment_type] || emp.employment_type}</span>}
              {emp.employee_code && <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>ID: {emp.employee_code}</span>}
            </div>
          </div>
          <div style={{ textAlign: 'right', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
            {emp.joining_date && <div>Joined {fmtDate(emp.joining_date)}</div>}
            {emp.work_location && <div style={{ marginTop: 2 }}>📍 {emp.work_location}</div>}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', marginBottom: 20 }}>
        {[{ key: 'details', label: 'Employment Details' }, { key: 'timeline', label: 'Career Timeline' }, { key: 'payslips', label: 'Salary Slips' }].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: '10px 24px', background: 'none', border: 'none', cursor: 'pointer',
            fontSize: '0.875rem', fontWeight: tab === t.key ? 700 : 500,
            color: tab === t.key ? 'var(--primary)' : 'var(--text-secondary)',
            borderBottom: tab === t.key ? '2px solid var(--primary)' : '2px solid transparent',
            marginBottom: -1,
          }}>{t.label}</button>
        ))}
      </div>

      {/* Employment Details */}
      {tab === 'details' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(440px, 1fr))', gap: 20 }}>
          <div>
            <Section title="Job Information">
              <InfoRow label="Employee Code"   value={fmt(emp.employee_code)} />
              <InfoRow label="Designation"     value={fmt(emp.designation)} />
              <InfoRow label="Department"      value={fmt(emp.department)} />
              <InfoRow label="Employment Type" value={EMP_TYPE_LABELS[emp.employment_type] || fmt(emp.employment_type)} />
              <InfoRow label="Work Location"   value={fmt(emp.work_location)} />
              <InfoRow label="Joining Date"    value={fmtDate(emp.joining_date)} />
              <InfoRow label="Confirmation"    value={fmtDate(emp.confirmation_date)} />
              <InfoRow label="Status"          value={statusMeta.label} />
            </Section>

            <Section title="Emergency Contact">
              <InfoRow label="Name"         value={fmt(emp.emergency_name)} />
              <InfoRow label="Relationship" value={fmt(emp.emergency_relation)} />
              <InfoRow label="Phone"        value={fmt(emp.emergency_phone)} />
            </Section>
          </div>

          <div>
            <Section title="Personal Information">
              <InfoRow label="Date of Birth"   value={fmtDate(emp.dob)} />
              <InfoRow label="Gender"          value={fmt(emp.gender)} />
              <InfoRow label="Blood Group"     value={fmt(emp.blood_group)} />
              <InfoRow label="Nationality"     value={fmt(emp.nationality)} />
              <InfoRow label="Marital Status"  value={fmt(emp.marital_status)} />
              <InfoRow label="Phone"           value={fmt(emp.phone)} />
              <InfoRow label="Email"           value={fmt(emp.email)} />
            </Section>

            <Section title="Address">
              <InfoRow label="Address"  value={fmt(emp.address)} />
              <InfoRow label="City"     value={fmt(emp.city)} />
              <InfoRow label="State"    value={fmt(emp.state)} />
              <InfoRow label="Pincode"  value={fmt(emp.pincode)} />
              <InfoRow label="Country"  value={fmt(emp.country)} />
            </Section>
          </div>
        </div>
      )}

      {/* Career Timeline */}
      {tab === 'timeline' && (
        <div className="card">
          <div className="card-body">
            <CareerTimeline endpoint="/hr/me/timeline" />
          </div>
        </div>
      )}

      {/* Salary Slips */}
      {tab === 'payslips' && (
        payslipsLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner spinner-lg" /></div>
        ) : payslips.length === 0 ? (
          <div className="card"><div className="card-body" style={{ textAlign: 'center', padding: 40 }}>
            <div style={{ fontSize: '2rem', marginBottom: 10 }}>📄</div>
            <div style={{ fontWeight: 600 }}>No salary slips available yet</div>
            <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginTop: 4 }}>Payslips appear here once payroll is processed by HR</div>
          </div></div>
        ) : (
          <div className="card">
            <div className="card-body" style={{ padding: 0 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--surface-2)', borderBottom: '2px solid var(--border)' }}>
                    {['Month', 'Gross Earned', 'PF', 'TDS', 'PT', 'Other Ded.', 'Net Pay', 'Status', ''].map(h => (
                      <th key={h} style={{ padding: '11px 14px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-muted)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {payslips.map((p, i) => {
                    const isProcessed = ['processed', 'paid'].includes(p.status);
                    const statusStyle = p.status === 'paid'
                      ? { background: '#d1fae5', color: '#065f46' }
                      : p.status === 'processed'
                      ? { background: '#dbeafe', color: '#1e40af' }
                      : { background: '#f1f5f9', color: '#64748b' };
                    return (
                      <tr key={p.id} style={{ borderBottom: '1px solid var(--border)', background: i % 2 === 0 ? '#fff' : 'var(--surface-2)' }}>
                        <td style={{ padding: '12px 14px', fontWeight: 600, fontSize: '0.875rem' }}>{fmtMonth(p.payroll_month)}</td>
                        <td style={{ padding: '12px 14px', fontSize: '0.875rem' }}>{fmtINR(p.gross_earned)}</td>
                        <td style={{ padding: '12px 14px', fontSize: '0.875rem', color: '#dc2626' }}>{p.pf_employee ? fmtINR(p.pf_employee) : '—'}</td>
                        <td style={{ padding: '12px 14px', fontSize: '0.875rem', color: '#dc2626' }}>{p.tds ? fmtINR(p.tds) : '—'}</td>
                        <td style={{ padding: '12px 14px', fontSize: '0.875rem', color: '#dc2626' }}>{p.pt ? fmtINR(p.pt) : '—'}</td>
                        <td style={{ padding: '12px 14px', fontSize: '0.875rem', color: '#dc2626' }}>{p.other_deductions ? fmtINR(p.other_deductions) : '—'}</td>
                        <td style={{ padding: '12px 14px', fontWeight: 700, fontSize: '0.875rem', color: '#059669' }}>{fmtINR(p.net_pay)}</td>
                        <td style={{ padding: '12px 14px' }}>
                          <span style={{ padding: '3px 8px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 600, ...statusStyle }}>
                            {p.status === 'paid' ? 'Paid' : p.status === 'processed' ? 'Processed' : p.status || 'Pending'}
                          </span>
                          {p.payment_date && (
                            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 2 }}>
                              {new Date(p.payment_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '12px 14px', textAlign: 'right' }}>
                          {isProcessed && (
                            <button
                              onClick={() => downloadPayslip(p.id, p.payroll_month)}
                              disabled={downloading === p.id}
                              style={{
                                display: 'inline-flex', alignItems: 'center', gap: 5,
                                padding: '6px 12px', borderRadius: 6, border: '1px solid var(--primary)',
                                background: downloading === p.id ? 'var(--surface-2)' : 'var(--primary-lighter)',
                                color: 'var(--primary)', fontSize: '0.78rem', fontWeight: 600,
                                cursor: downloading === p.id ? 'not-allowed' : 'pointer',
                              }}
                            >
                              {downloading === p.id ? (
                                <span className="spinner" style={{ width: 12, height: 12 }} />
                              ) : (
                                <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/>
                                </svg>
                              )}
                              {downloading === p.id ? 'Downloading…' : 'Download PDF'}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )
      )}
    </div>
  );
}
